import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent
} from "react";
import { Check, Images, Link2 } from "lucide-react";

import type { Difficulty } from "@/domain";
import type { ActivityHostRenderContext } from "@/features/activity-host";
import type { CurriculumAudioPlayer } from "@/features/audio";
import type {
  MatchingItemKey,
  MatchingItemReference
} from "@/game/templates/choice-match";

import type {
  PictureCharacterAction,
  PictureCharacterRenderModel
} from "../adapters";
import {
  ChineseDifficultyBadge,
  ChineseFeedbackNotice,
  ManifestImage
} from "./shared";

export interface PictureCharacterRendererProps {
  readonly audioPlayer: CurriculumAudioPlayer;
  readonly context: ActivityHostRenderContext<
    PictureCharacterAction,
    PictureCharacterRenderModel
  >;
  readonly difficulty: Difficulty;
}

export function PictureCharacterRenderer({
  audioPlayer,
  context,
  difficulty
}: PictureCharacterRendererProps) {
  const headingId = useId();
  const itemRefs = useRef(new Map<MatchingItemKey, HTMLButtonElement>());
  const { dispatch, getActivationProps, model } = context;
  const firstItemKey = model.keyboardOrder[0] ?? null;

  useEffect(() => {
    if (model.focusedItemKey !== null) {
      itemRefs.current.get(model.focusedItemKey)?.focus();
    }
  }, [model.focusedItemKey]);

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    item: MatchingItemReference
  ): void {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        dispatch(
          { type: "move-focus", direction: "next" },
          "keyboard"
        );
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        dispatch(
          { type: "move-focus", direction: "previous" },
          "keyboard"
        );
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        dispatch(
          { type: "select-item", item, inputMethod: "keyboard" },
          "keyboard"
        );
        break;
    }
  }

  function getTabIndex(
    itemKey: MatchingItemKey,
    focused: boolean
  ): 0 | -1 {
    return focused ||
      (model.focusedItemKey === null && itemKey === firstItemKey)
      ? 0
      : -1;
  }

  return (
    <section
      aria-labelledby={headingId}
      className="chinese-player__activity chinese-player__matching"
      data-difficulty={difficulty}
      data-reduced-motion={context.reducedMotion}
    >
      <header className="chinese-player__activity-heading">
        <span aria-hidden="true" className="chinese-player__activity-mark">
          <Images />
        </span>
        <div>
          <ChineseDifficultyBadge difficulty={difficulty} />
          <h2 id={headingId}>先选图片，再选汉字</h2>
        </div>
        <span
          aria-label={
            "已配对 " +
            String(model.progress.matched) +
            " 组，共 " +
            String(model.progress.total) +
            " 组"
          }
          className="chinese-player__match-progress"
          role="status"
        >
          <Link2 aria-hidden="true" />
          {model.progress.matched} / {model.progress.total}
        </span>
      </header>

      <div className="chinese-player__match-board">
        <div
          aria-label="图片"
          className="chinese-player__picture-options"
          role="group"
        >
          {model.pictures.filter(({ visible }) => visible).map((picture) => {
            const item = {
              side: "left",
              itemId: picture.id
            } as const;

            return (
              <button
                {...getActivationProps((inputMethod) => ({
                  type: "select-item",
                  item,
                  inputMethod
                }))}
                aria-label={"选择图片：" + picture.alt}
                aria-pressed={picture.selected}
                className="chinese-player__picture-choice"
                data-hinted={picture.hinted}
                data-matched={picture.matched}
                data-selected={picture.selected}
                disabled={picture.disabled}
                key={picture.id}
                onFocus={() =>
                  dispatch(
                    {
                      type: "focus-item",
                      itemKey: picture.itemKey
                    },
                    "keyboard"
                  )
                }
                onKeyDown={(event) => handleKeyDown(event, item)}
                ref={(node) => {
                  if (node === null) {
                    itemRefs.current.delete(picture.itemKey);
                  } else {
                    itemRefs.current.set(picture.itemKey, node);
                  }
                }}
                tabIndex={getTabIndex(
                  picture.itemKey,
                  picture.focused
                )}
                type="button"
              >
                <ManifestImage
                  alt={picture.alt}
                  src={picture.imageSrc}
                />
                <span className="chinese-player__match-label">
                  {picture.alt}
                </span>
                <span className="chinese-player__match-status">
                  {picture.matched ? (
                    <>
                      <Check aria-hidden="true" />
                      已配对
                    </>
                  ) : picture.hinted ? (
                    "提示"
                  ) : picture.selected ? (
                    "已选中"
                  ) : (
                    "图片"
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div
          aria-label="汉字"
          className="chinese-player__character-options"
          role="group"
        >
          {model.characters
            .filter(({ visible }) => visible)
            .map((character) => {
              const item = {
                side: "right",
                itemId: character.id
              } as const;

              return (
                <button
                  {...getActivationProps((inputMethod) => ({
                    type: "select-item",
                    item,
                    inputMethod
                  }))}
                  aria-label={"选择汉字：" + character.label}
                  aria-pressed={character.selected}
                  className="chinese-player__character-choice"
                  data-hinted={character.hinted}
                  data-matched={character.matched}
                  data-selected={character.selected}
                  disabled={character.disabled}
                  key={character.id}
                  onFocus={() =>
                    dispatch(
                      {
                        type: "focus-item",
                        itemKey: character.itemKey
                      },
                      "keyboard"
                    )
                  }
                  onKeyDown={(event) => handleKeyDown(event, item)}
                  ref={(node) => {
                    if (node === null) {
                      itemRefs.current.delete(character.itemKey);
                    } else {
                      itemRefs.current.set(character.itemKey, node);
                    }
                  }}
                  tabIndex={getTabIndex(
                    character.itemKey,
                    character.focused
                  )}
                  type="button"
                >
                  <strong>{character.label}</strong>
                  <span className="chinese-player__match-status">
                    {character.matched ? (
                      <>
                        <Check aria-hidden="true" />
                        已配对
                      </>
                    ) : character.hinted ? (
                      "提示"
                    ) : character.selected ? (
                      "已选中"
                    ) : (
                      "汉字"
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      </div>

      <ChineseFeedbackNotice
        audioPlayer={audioPlayer}
        enabled={!context.isPaused}
        feedback={model.feedback}
      />
    </section>
  );
}
